import { useState } from "react";

import { Button, TextField, Typography } from "@mui/material";
import classNames from "classnames";
import { IoSync } from "react-icons/io5";

import { ECourseFrequency } from "../../../../../@types";
import { validators } from "../../../../../utils";
import { useMyCourseInstructor } from "../../hooks/useMyCourseInstructor";

export function MyCoursePace() {
	const { course, updateCourseBasicInformation } = useMyCourseInstructor();
	const [numberOfClasses, setNumberOfClasses] = useState(
		`${course?.classesPerDay ?? ""}`,
	);
	const [numberOfClassesError, setNumberOfClassesError] = useState("");
	const [frequency, setFrequency] = useState(
		course?.frequency ?? ECourseFrequency.DAILY,
	);
	const [loading, setLoading] = useState(false);

	const onClickCancel = () => {
		setNumberOfClasses(`${course?.classesPerDay ?? ""}`);
		setFrequency(course?.frequency ?? ECourseFrequency.DAILY);
	};

	const onClickUpdate = async () => {
		if (isDisabled) {
			return;
		}

		setLoading(true);
		await updateCourseBasicInformation({
			classesPerDay: Number(numberOfClasses),
			frequency,
		});
		setLoading(false);
	};

	const isDisabled =
		((Number(numberOfClasses) === course?.classesPerDay ||
			Number(numberOfClasses) === 0) &&
			frequency === course?.frequency) ||
		loading;
	const isEdited =
		(Number(numberOfClasses) !== course?.classesPerDay ||
			frequency !== course.frequency) &&
		!loading;

	return (
		<>
			<div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
				<Typography className="small-title-text bold">
					1. What pace would make training most effective?
				</Typography>
				{loading && (
					<div>
						<IoSync className="normal-icon rotation-icon blue-icon" />
					</div>
				)}
			</div>
			<Typography className="normal-text">
				Give students a clear progression path. From setting appointments, to
				covering online class material, students will be encouraged to follow
				your recommended pace.
			</Typography>
			<div className="common-input">
				<div className="course-pace-input-container item-separator-small">
					<TextField
						size="small"
						autoComplete="off"
						id="course-pace"
						placeholder="n"
						style={{ width: "64px" }}
						onChange={(e) => {
							if (
								e.target.value !== "" &&
								!validators.integer.test(e.target.value)
							) {
								return;
							}

							if (e.target.value.length > 2) {
								return;
							}

							setNumberOfClasses(e.target.value);
							if (Number(e.target.value) === 0) {
								setNumberOfClassesError(
									"Please enter an integer greater than 0",
								);
							} else {
								setNumberOfClassesError("");
							}
						}}
						className={classNames("", {
							invalid: !!numberOfClassesError,
						})}
						value={numberOfClasses}
						slotProps={{
							input: {
								className: "normal-text",
								classes: {
									input: "input-placeholder",
								},
								inputMode: "numeric",
							},
						}}
					/>
					{numberOfClasses === "1" && (
						<Typography className="normal-text">Class every</Typography>
					)}
					{numberOfClasses !== "1" && (
						<Typography className="normal-text">Classes every</Typography>
					)}
					<Typography
						className={classNames("normal-text frequency-button", {
							selected: frequency === ECourseFrequency.DAILY,
						})}
						id="course-frequency-day"
						onClick={() => {
							setFrequency(ECourseFrequency.DAILY);
						}}>
						{" day "}
					</Typography>
					<Typography
						className={classNames("normal-text frequency-button", {
							selected: frequency === ECourseFrequency.WEEKLY,
						})}
						id="course-frequency-week"
						onClick={() => {
							setFrequency(ECourseFrequency.WEEKLY);
						}}>
						{" week "}
					</Typography>
					<Typography className="normal-text">until completion.</Typography>
				</div>
				{numberOfClassesError && (
					<Typography className="small-text invalid-message">
						{numberOfClassesError}
					</Typography>
				)}
			</div>
			<div className="button-container item-separator-small">
				<Button
					className="normal-button disabled-button"
					disabled={!isEdited}
					onClick={onClickCancel}>
					Cancel
				</Button>
				<Button
					disabled={isDisabled}
					className={classNames("normal-button", {
						"primary-button": !isDisabled,
						"disabled-button": isDisabled,
					})}
					onClick={onClickUpdate}>
					Save
				</Button>
			</div>
		</>
	);
}
